import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { useSessionManager } from '../hooks/useSessionManager';

interface AdminProfile {
  uid: string;
  email: string;
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  adminProfile: AdminProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useSessionManager({ user: currentUser });

  const validateAdmin = async (uid: string): Promise<boolean> => {
    const adminRef = ref(database, `plataforma/admins/${uid}`);
    const snapshot = await get(adminRef);
    return snapshot.exists() && snapshot.val() === true;
  };

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    const isAdmin = await validateAdmin(user.uid);
    if (!isAdmin) {
      await signOut(auth);
      throw new Error('Acesso negado. Você não tem permissão para acessar este painel.');
    }

    const profile: AdminProfile = {
      uid: user.uid,
      email: user.email || email,
    };

    setAdminProfile(profile);
    localStorage.setItem('sp_master_admin', JSON.stringify(profile));
  };

  const logout = async () => {
    await signOut(auth);
    setAdminProfile(null);
    setCurrentUser(null);
    localStorage.removeItem('sp_master_admin');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        const saved = localStorage.getItem('sp_master_admin');
        if (saved) {
          try {
            const parsed = JSON.parse(saved) as AdminProfile;
            if (parsed.uid === user.uid) {
              const isAdmin = await validateAdmin(user.uid);
              if (isAdmin) {
                setAdminProfile(parsed);
              } else {
                localStorage.removeItem('sp_master_admin');
                await signOut(auth);
              }
            }
          } catch {
            localStorage.removeItem('sp_master_admin');
          }
        }
      } else {
        setAdminProfile(null);
        localStorage.removeItem('sp_master_admin');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    currentUser,
    adminProfile,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

